import { useEffect, useState, useContext } from "react";
import { AuthContext } from "../../auth/AuthContext";
import { usePortalData } from "../context/PortalDataContext";
import { InputText } from "primereact/inputtext";
import { Dropdown }  from "primereact/dropdown";

export default function PharmacySelectorHeader() {
  const { orgId, departmentId, selectOrg, selectDepartment } = useContext(AuthContext);
  const {
    pharmacies,
    myDepartments,
    allDepartments,
    setSelectedPharmacyName,
    setSelectedDepartmentName,
    refresh,   
  } = usePortalData();

  const [selectedPharmacy,   setSelectedPharmacy]   = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [showAllBranches,    setShowAllBranches]     = useState(false);

  const departments = showAllBranches ? allDepartments : myDepartments;

  // ── Auto-select pharmacy and department from context data ─────────────
  useEffect(() => {
    if (!pharmacies.length) return;

    // Restore from stored orgId (returning user) or auto-select single pharmacy
    let pharmacy = null;
    if (orgId) {
      pharmacy = pharmacies.find(p => p.organization_id === orgId) ?? null;
    }
    if (!pharmacy && pharmacies.length === 1) {
      pharmacy = pharmacies[0];
      selectOrg(pharmacy.organization_id); // triggers orgId → dept fetch
    }
    if (pharmacy) {
      setSelectedPharmacy(pharmacy);
      setSelectedPharmacyName(pharmacy.name);
    }
  }, [pharmacies]);

  // ── Auto-select department from context data ──────────────────────────
  useEffect(() => {
    if (!myDepartments.length && !allDepartments.length) return;

    let selected = null;
    if (departmentId) {
      selected = allDepartments.find(d => d.department_id === departmentId);
    }
    if (!selected && myDepartments.length === 1) {
      selected = myDepartments[0];
      selectDepartment(myDepartments[0].department_id);
    }

    if (selected) {
      setSelectedDepartment(selected);
      setSelectedDepartmentName(selected.name);
    }
  }, [myDepartments, allDepartments, departmentId]);

  // ── Pharmacy changed ──────────────────────────────────────────────────
  const onPharmacyChange = (p) => {
    setSelectedPharmacy(p);
    setSelectedPharmacyName(p.name);
    setSelectedDepartment(null);
    setSelectedDepartmentName(null);
    setShowAllBranches(false);
    selectOrg(p.organization_id);
    // Departments for new org will be fetched by PortalDataContext
    // when orgId changes via selectOrg
  };

  // ── Department changed ────────────────────────────────────────────────
  const onDepartmentChange = (d) => {
    setSelectedDepartment(d);
    setSelectedDepartmentName(d.name);
    selectDepartment(d.department_id);
  };

  // ── Toggle show all ───────────────────────────────────────────────────
  const onToggleShowAll = (checked) => {
    setShowAllBranches(checked);
    if (!checked && selectedDepartment) {
      const stillAvailable = myDepartments.find(
        d => d.department_id === selectedDepartment.department_id
      );
      if (!stillAvailable) {
        const fallback = myDepartments[0] ?? null;
        setSelectedDepartment(fallback);
        if (fallback) {
          setSelectedDepartmentName(fallback.name);
          selectDepartment(fallback.department_id);
        } else {
          setSelectedDepartmentName(null);
        }
      }
    }
  };

  // ── Templates (unchanged) ─────────────────────────────────────────────
  const deptItemTemplate = (option) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
      <span>{option.name}</span>
      {option.is_mine && (
        <span style={{
          fontSize: 10, fontWeight: 600, padding: "1px 6px", borderRadius: 20,
          background: "var(--brand-light)", color: "var(--brand-primary)",
          lineHeight: 1.6,
        }}>My branch</span>
      )}
    </div>
  );

  const deptPanelFooter = () => (
    <div
      style={{
        padding: "8px 12px 10px", borderTop: "1px solid var(--border-light)",
        display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
      }}
      onClick={() => onToggleShowAll(!showAllBranches)}
    >
      <div style={{
        width: 16, height: 16, borderRadius: 4, flexShrink: 0,
        border: `2px solid ${showAllBranches ? "var(--brand-primary)" : "var(--border-color)"}`,
        background: showAllBranches ? "var(--brand-primary)" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center",
        transition: "all 0.15s",
      }}>
        {showAllBranches && <i className="pi pi-check" style={{ fontSize: 9, color: "white" }} />}
      </div>
      <span style={{ fontSize: 12.5, color: "var(--text-secondary)", userSelect: "none" }}>
        Show all branches
      </span>
    </div>
  );

  return (
    <div className="selector-wrapper">
      {pharmacies.length === 1 ? (
        <InputText value={pharmacies[0]?.name ?? ""} readOnly style={{ width: 160, fontSize: 13 }} />
      ) : (
        <Dropdown
          value={selectedPharmacy}
          options={pharmacies}
          optionLabel="name"
          onChange={(e) => onPharmacyChange(e.value)}
          placeholder="Select pharmacy"
          style={{ minWidth: 160 }}
        />
      )}

      {selectedPharmacy && (
        <Dropdown
          value={selectedDepartment}
          options={departments}
          optionLabel="name"
          dataKey="department_id"
          onChange={(e) => onDepartmentChange(e.value)}
          placeholder="Select branch"
          itemTemplate={deptItemTemplate}
          panelFooterTemplate={deptPanelFooter}
          style={{ minWidth: 160 }}
          emptyMessage="No branches found"
        />
      )}
    </div>
  );
}